import { z } from 'zod';
import { rubroTipoEnum, unidadEnum, proformaStateEnum } from './enums';

export const createProformaSchema = z.object({
  projectId: z.string().uuid('ID de proyecto inválido'),
  observaciones: z.string().max(2000).optional().nullable(),
  marcas: z.string().max(2000).optional().nullable(),
  formaPago: z.string().max(500).optional().nullable(),
  plazo: z.string().max(500).optional().nullable(),
  tiempoEntrega: z.string().max(500).optional().nullable(),
  descuento: z.number().min(0, 'El descuento no puede ser negativo').default(0),
  ivaRate: z.number().min(0).max(100, 'El IVA debe estar entre 0 y 100').default(15),
  medidaSetId: z.string().uuid().optional().nullable(),
});

export type CreateProformaInput = z.infer<typeof createProformaSchema>;

export const updateProformaSchema = createProformaSchema.omit({ projectId: true }).partial();
export type UpdateProformaInput = z.infer<typeof updateProformaSchema>;

export const listProformasSchema = z.object({
  search: z.string().optional(),
  estado: proformaStateEnum.optional(),
  projectId: z.string().uuid().optional(),
  clientId: z.string().uuid().optional(),
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  month: z.coerce.number().int().min(1).max(12).optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
});
export type ListProformasInput = z.infer<typeof listProformasSchema>;

export const proformaParamsSchema = z.object({
  id: z.string().uuid('ID de proforma inválido'),
});
export type ProformaParams = z.infer<typeof proformaParamsSchema>;

export const proformaItemParamsSchema = z.object({
  id: z.string().uuid('ID de proforma inválido'),
  itemId: z.string().uuid('ID de ítem inválido'),
});
export type ProformaItemParams = z.infer<typeof proformaItemParamsSchema>;

// ── Proforma Items ──
const proformaItemSchema = z.object({
  id: z.string().uuid().optional().nullable(),
  recipeId: z.string().uuid().optional().nullable(),
  codigo: z.string().min(1, 'El código es requerido').max(50),
  descripcion: z.string().min(1, 'La descripción es requerida').max(500),
  descripcionProyecto: z.string().max(500).optional().nullable(),
  categoria: rubroTipoEnum,
  unidad: unidadEnum.default('m2'),
  cantidad: z.number().min(0, 'La cantidad no puede ser negativa'),
  precioMo: z.number().min(0, 'El precio de mano de obra no puede ser negativo'),
  precioMateriales: z.number().min(0, 'El precio de materiales no puede ser negativo'),
  porcentajeUtilidad: z.number().min(0).max(100, 'La utilidad debe estar entre 0% y 100%').default(0),
  aplicacionId: z.string().uuid().optional().nullable(),
  orden: z.number().int().default(0),
});

export const createProformaItemSchema = proformaItemSchema.extend({
  proformaId: z.string().uuid('ID de proforma inválido'),
});
export type CreateProformaItemInput = z.infer<typeof createProformaItemSchema>;

export const updateProformaItemSchema = proformaItemSchema.partial();
export type UpdateProformaItemInput = z.infer<typeof updateProformaItemSchema>;

// Batch upsert: items with id=null are inserted, with id are updated
export const batchProformaItemsSchema = z.object({
  items: z.array(proformaItemSchema),
});
export type BatchProformaItemsInput = z.infer<typeof batchProformaItemsSchema>;

// ── Copy proforma ──
export const copyProformaSchema = z.object({
  targetProjectId: z.string().uuid('ID de proyecto destino invalido'),
  copyCondiciones: z.boolean().default(false),
});
export type CopyProformaInput = z.infer<typeof copyProformaSchema>;

// ── Proforma transitions ──
export const sendProformaSchema = z.object({});
export const approveProformaSchema = z.object({});
export const rejectProformaSchema = z.object({
  motivoRechazo: z.string().min(1, 'El motivo de rechazo es requerido').max(1000),
});

export type RejectProformaInput = z.infer<typeof rejectProformaSchema>;
